/**
 * ProfileSetupNotice — shown when an authenticated AP has no usable AP_Master
 * profile (or the profile read failed). Self-diagnosing: it shows the exact
 * signed-in identity so the AP can send it to their Care Manager, who sets it
 * as AP_Master.Email — removing the guesswork from onboarding.
 *
 * The heading and body come from `describeAPIdentityState`, not from branching
 * in here, so the shell and the four AP pages say ONE thing about identity in
 * one voice — and so the decision is testable, which JSX in this repo is not.
 *
 * Under `isDebugEnabled()` it adds the two facts that would have ended the
 * 2026-09-02 hunt in minutes: the RESOLVED provider code, and every candidate
 * row as `id · APCode · Status` — the same PII-free descriptors the server
 * writes to its `[ap-identity]` log line, so the screen and the log can be
 * compared word for word.
 */

import { Icon } from '../ui';
import { isDebugEnabled } from '../../config/debug';
import { describeAPIdentityState, formatAPCandidate } from './apIdentityState';
import type { APIdentityCandidate, APIdentityOutcome } from '../../services/currentAPResolution';
import styles from './ProfileSetupNotice.module.css';

interface ProfileSetupNoticeProps {
  /** The signed-in identity (account.username). */
  email: string | null;
  /** Set when the profile lookup errored (vs. simply not matching). */
  error: string | null;
  /** WHY the app looks like this — drives the heading and body. */
  outcome?: APIdentityOutcome | null;
  /** The resolved provider code, when one resolved. */
  apCode?: string | null;
  /** The resolved record's Title, when one resolved. */
  apName?: string | null;
  /** The resolved record's Status, when it is known. */
  status?: string | null;
  /** More than one owned row shares the winning status band. */
  ambiguous?: boolean;
  /** Diagnostic: PII-free descriptor of every owned row, winner first. */
  candidates?: APIdentityCandidate[];
  /** Diagnostic: how many provider records the lookup read. */
  recordsRead?: number | null;
  /** Diagnostic: the identity forms the app matched on. */
  identities?: string[];
  /** Diagnostic: whether the Graph connection is ready. */
  graphReady?: boolean;
  /** Diagnostic: Graph connection error, if any. */
  graphError?: string | null;
  /**
   * Banner form: no retry button, tighter padding. Used for the debug-only
   * ambiguity notice, which rides ABOVE a working app and must not look like
   * the app has stopped.
   */
  compact?: boolean;
  /** Omit to render no retry button (the compact/banner form). */
  onRetry?: () => void;
}

export default function ProfileSetupNotice({
  email,
  error,
  outcome,
  apCode,
  apName,
  status,
  ambiguous,
  candidates,
  recordsRead,
  identities,
  graphReady,
  graphError,
  compact,
  onRetry,
}: ProfileSetupNoticeProps) {
  // Raw Graph diagnostics are for admins/onboarding only — never shown to AP
  // guests in production. Gate behind the debug flag (?debug=1 / dev build).
  const debug = isDebugEnabled();

  const state = describeAPIdentityState(outcome, { apCode, status, ambiguous, apName });

  return (
    <div className={`${styles.wrap} ${compact ? styles.wrapCompact : ''}`}>
      <div className={`${styles.card} ${compact ? styles.cardCompact : ''}`}>
        <span className={styles.icon}>
          <Icon name="shield" size={30} />
        </span>

        {error ? (
          <>
            <h2 className={styles.title}>Just a moment</h2>
            <p className={styles.body}>
              We couldn&rsquo;t load your provider profile just now — this is usually a
              brief access hiccup. Please try again in a moment.
            </p>
            {debug && <p className={styles.detail}>{error}</p>}
          </>
        ) : (
          <>
            <h2 className={styles.title}>{state.heading}</h2>
            <p className={styles.body}>You&rsquo;re signed in as</p>
            <code className={styles.identity}>{email || 'unknown'}</code>
            <p className={styles.body}>{state.body}</p>
          </>
        )}

        {onRetry && (
          <button className={styles.retry} onClick={onRetry} type="button">
            Try again
          </button>
        )}

        {!error && debug && (
          <p className={styles.diag}>
            {graphReady === false && 'Connection not ready. '}
            {graphError && `Connection error: ${graphError}. `}
            {/* The resolved code and the candidate rows: the two facts that
                would have ended the ISS-032 hunt in minutes. */}
            {`Resolved provider code: ${apCode || 'none'}. `}
            {candidates && candidates.length > 0 &&
              `Candidate records (winner first): ${candidates.map(formatAPCandidate).join(', ')}. `}
            {recordsRead != null && `Checked ${recordsRead} provider record${recordsRead === 1 ? '' : 's'}. `}
            {identities && identities.length > 0 && `Matching on: ${identities.join(', ')}.`}
          </p>
        )}
      </div>
    </div>
  );
}
